import { format, formatDistanceToNow, parseISO } from "date-fns"
import { enGB, fr } from "date-fns/locale"

const LOCALES = { en: enGB, fr } as const
export type SupportedLocale = keyof typeof LOCALES

const toDate = (value: string | Date): Date => (typeof value === "string" ? parseISO(value) : value)

/**
 * Absolute, in the operator's timezone. FR-DSH-XCT-07 requires an absolute
 * value to be AVAILABLE on every timestamp -- a bare "2 hours ago" on a ledger
 * entry or an audit record is not good enough.
 *
 * PDD §18 Q-06 leaves the timezone policy open; until it is answered this uses
 * the operator's local zone.
 */
export function formatDateTime(value: string | Date, locale: SupportedLocale): string {
  return format(toDate(value), "d MMM yyyy, HH:mm", { locale: LOCALES[locale] })
}

export function formatDate(value: string | Date, locale: SupportedLocale): string {
  return format(toDate(value), "d MMM yyyy", { locale: LOCALES[locale] })
}

/** Secondary to the absolute value, never a replacement for it. */
export function formatRelative(value: string | Date, locale: SupportedLocale): string {
  return formatDistanceToNow(toDate(value), { addSuffix: true, locale: LOCALES[locale] })
}

/** Sortable and unambiguous, for exports and filenames. */
export function formatIsoDate(value: string | Date): string {
  return format(toDate(value), "yyyy-MM-dd")
}

/**
 * `<input type="datetime-local">` reads and writes the operator's local time
 * with no timezone suffix (`yyyy-MM-ddTHH:mm`), not an ISO instant -- these
 * convert between that and the ISO 8601 the contract's `date-time` fields
 * require, so a discount's validity window round-trips through the form.
 */
export function toDatetimeLocalValue(iso: string | undefined): string {
  if (!iso) return ""
  return format(toDate(iso), "yyyy-MM-dd'T'HH:mm")
}

export function fromDatetimeLocalValue(local: string): string | undefined {
  if (local === "") return undefined
  return new Date(local).toISOString()
}
